/**
 * Multi-currency helpers for seller/admin stats displays.
 * Stats may send `currency` / `balance_currency` (e.g. eur, usd, pln).
 */

const FALLBACK_CURRENCY = "USD";

const CURRENCY_LOCALES = {
  PLN: "pl-PL",
  EUR: "de-DE",
  USD: "en-US",
  GBP: "en-GB",
  CHF: "de-CH",
  JPY: "ja-JP",
  CAD: "en-CA",
  AUD: "en-AU",
  INR: "en-IN",
  SEK: "sv-SE",
  NOK: "nb-NO",
  DKK: "da-DK",
  CZK: "cs-CZ",
  HUF: "hu-HU",
  RON: "ro-RO",
  BGN: "bg-BG",
};

/** Hardcoded glyphs when Intl is unavailable or returns a plain code. */
const CURRENCY_GLYPHS = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  PLN: "zł",
  JPY: "¥",
  CNY: "¥",
  CHF: "Fr",
  CAD: "C$",
  AUD: "A$",
  INR: "₹",
  SEK: "kr",
  NOK: "kr",
  DKK: "kr",
  CZK: "Kč",
  HUF: "Ft",
  RON: "lei",
  BGN: "лв",
};

export function normalizeCurrencyCode(code) {
  const normalized = String(code ?? "")
    .trim()
    .toUpperCase();
  return normalized || FALLBACK_CURRENCY;
}

/** Resolve display currency from a /stats payload. */
export function getStatsCurrency(data) {
  return normalizeCurrencyCode(
    data?.balance_currency || data?.currency || FALLBACK_CURRENCY
  );
}

function getLocaleForCurrency(currencyCode) {
  const code = normalizeCurrencyCode(currencyCode);
  return CURRENCY_LOCALES[code] || "en-US";
}

/**
 * Compact currency symbol/glyph for dashboard icons.
 * @param {string} currencyCode
 * @returns {string}
 */
export function getCurrencyGlyph(currencyCode) {
  const code = normalizeCurrencyCode(currencyCode);

  if (CURRENCY_GLYPHS[code]) {
    return CURRENCY_GLYPHS[code];
  }

  try {
    const parts = new Intl.NumberFormat(getLocaleForCurrency(code), {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(1);
    const symbol = parts.find((part) => part.type === "currency")?.value;
    if (symbol && symbol.toUpperCase() !== code) {
      return symbol;
    }
  } catch {
    // Invalid currency code — fall through
  }

  return code;
}

/**
 * Format an amount in the given currency.
 * @param {number|string|null|undefined} amount
 * @param {string} [currencyCode]
 * @returns {string}
 */
export function formatCurrencyAmount(amount, currencyCode = FALLBACK_CURRENCY) {
  if (amount === null || amount === undefined || amount === "") {
    return "—";
  }

  const value = parseFloat(amount);
  if (Number.isNaN(value)) return "—";

  const code = normalizeCurrencyCode(currencyCode);

  try {
    return value.toLocaleString(getLocaleForCurrency(code), {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } catch {
    return `${value.toFixed(2)} ${code}`;
  }
}

/** @deprecated Prefer formatCurrencyAmount — kept for existing call sites. */
export function formatPlnAmount(amount, currencyCode = "PLN") {
  return formatCurrencyAmount(amount, currencyCode);
}

/** Earnings from stats payload. */
export function getEarnings(data) {
  return data?.total_earnings_pln ?? data?.total_earnings;
}

/** Available (payout-ready) balance from stats payload. */
export function getAvailableBalance(data) {
  return (
    data?.available_balance ??
    data?.balance_pln ??
    data?.balance
  );
}

/** Pending (not yet cleared) balance from stats payload. */
export function getPendingBalance(data) {
  return data?.pending_balance ?? 0;
}

/** @deprecated Prefer getAvailableBalance — kept for existing call sites. */
export function getBalance(data) {
  return getAvailableBalance(data);
}

/** Cash-out amount for transfer API — available only, never pending. */
export function getCashOutBalance(data) {
  return getAvailableBalance(data);
}

/** Minimum cash-out threshold from stats. */
export function getMinCashout(data) {
  return data?.min_cashout ?? data?.min_cashout_pln;
}

/** @deprecated Prefer getEarnings */
export function getPlnEarnings(data) {
  return getEarnings(data);
}

/** @deprecated Prefer getBalance */
export function getPlnBalance(data) {
  return getBalance(data);
}
